import { useEffect } from "react";
import type { PublicSessionState } from "../types";
import { playLose, playWin, playFail } from "../audio";

interface AARProps {
  state: PublicSessionState;
  isIm?: boolean;
  onRematch?: () => void;
  onVoteRematch?: () => void;
}

export function AARView({ state, isIm, onRematch, onVoteRematch }: AARProps) {
  const aar = state.aar;
  const myTeam = state.myTeam;
  const myTeamPerf = aar?.perTeam.find((t) => t.teamId === myTeam?.id);
  const winningTeam = aar?.perTeam.find((t) => t.teamId === aar?.winningTeamId);

  // Per-viewer outcome:
  //  WON     — your team is the race winner.
  //  CLOSED  — your team identified all 4 but didn't win the race (rare).
  //  FAILED  — your team did not identify all 4 (whether the game ended on
  //            someone else's win or on global turn-exhaustion).
  //  WATCH   — you don't have a team (IM / spectator).
  type Verdict = "won" | "closed" | "failed" | "watch";
  const verdict: Verdict = (() => {
    if (!myTeamPerf) return "watch";
    if (myTeamPerf.teamId === aar?.winningTeamId) return "won";
    if (myTeamPerf.found.length >= 4) return "closed";
    return "failed";
  })();

  useEffect(() => {
    if (!aar) return;
    if (verdict === "won") playWin();
    else if (verdict === "closed") playWin();
    else if (verdict === "failed") {
      playLose();
      // Slight delay so the win fanfare for the OTHER teams (if any) doesn't
      // bleed into this team's loss sting.
    }
    else if (verdict === "watch") {
      if (aar.outcome === "won") playWin();
      else playLose();
    }
    // Replay the fail thud once more after the lose dirge for the team that
    // ran out of turns — drives the "we did not contain it" point home.
    if (verdict === "failed") {
      const t = setTimeout(() => playFail(), 1400);
      return () => clearTimeout(t);
    }
    return undefined;
    // Intentionally keyed on the stable outcome, not the whole `aar` object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aar?.outcome, verdict]);

  if (!aar) return null;

  const stamp = (() => {
    switch (verdict) {
      case "won": return "Your team contained the incident — first";
      case "closed": return "Your team contained the incident";
      case "failed":
        return aar.winningTeamId
          ? "Your team did not contain the incident"
          : "Incident not contained — no team closed the chain";
      case "watch":
        return aar.outcome === "won" ? "Incident contained" : "Incident not contained";
    }
  })();

  const headline = (() => {
    switch (verdict) {
      case "won":
        return (
          <>
            <span className="winner">{myTeamPerf?.teamName}</span> closed the chain in{" "}
            {myTeamPerf?.turnsTaken} turns. First on the board.
          </>
        );
      case "closed":
        return (
          <>
            Your team — <span className="winner">{myTeamPerf?.teamName}</span> — closed the chain
            in {myTeamPerf?.turnsTaken} turns.
            {winningTeam ? <> {winningTeam.teamName} got there first.</> : null}
          </>
        );
      case "failed":
        if (aar.winningTeamId && winningTeam) {
          return (
            <>
              <span className="winner">{winningTeam.teamName}</span> closed it in{" "}
              {winningTeam.turnsTaken} turns. Your team identified {myTeamPerf!.found.length}/4 in{" "}
              {myTeamPerf!.turnsTaken}/{aar.finalTurn} turns.
            </>
          );
        }
        return (
          <>
            The window closed before <span className="winner">any team</span> closed the chain.
            Your team identified {myTeamPerf!.found.length}/4 in {myTeamPerf!.turnsTaken} turns.
          </>
        );
      case "watch":
        return aar.outcome === "won" && winningTeam ? (
          <>
            <span className="winner">{winningTeam.teamName}</span> closed the chain in{" "}
            {winningTeam.turnsTaken} turns.
          </>
        ) : (
          <>The window closed before <span className="winner">any team</span> closed the chain.</>
        );
    }
  })();

  const lede = (() => {
    switch (verdict) {
      case "won":
        return "You ran a clean drill. The AAR below is your record — show your peers.";
      case "closed":
        return "You contained it. The race went to another team, but the procedures landed.";
      case "failed":
        return "It's a training range. The chain is in the AAR below — read your misses, not the hits. The clock was the teacher.";
      case "watch":
        return aar.outcome === "won"
          ? "Take a breath. The brief lands now — every roll is in the AAR below."
          : "The clock won this one. Read the misses below.";
    }
  })();

  return (
    <>
      <section className={`celebration ${verdict === "failed" || verdict === "watch" && aar.outcome === "lost" ? "lost" : ""}`}>
        <div className="stamp">{stamp}</div>
        <h2>{headline}</h2>
        <p>{lede}</p>

        <div className="vote-bar">
          {state.rematchVotes !== undefined && (
            <span className="vote-count">
              <strong>{state.rematchVotes}</strong> player{state.rematchVotes === 1 ? "" : "s"} voted
              for another round
            </span>
          )}
          {!isIm && onVoteRematch && (
            <button
              onClick={onVoteRematch}
              disabled={state.iVoted}
              className={state.iVoted ? "" : "primary"}
            >
              {state.iVoted ? "✓ You voted" : "↻ Another round"}
            </button>
          )}
          {isIm && onRematch && (
            <button className="primary" onClick={onRematch}>
              ↻ Spin up rematch — same teams
            </button>
          )}
        </div>
      </section>

      <section className="panel">
        <h2>After-Action Report</h2>

        <h3>The actual attack chain</h3>
        <div className="chain" style={{ marginBottom: 16 }}>
          {aar.attackChain.map((c) => (
            <div key={c.id} className="stage revealed">
              <div className="label">
                {c.stage}
                {c.attck && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 10,
                      color: "var(--color-accent)",
                      letterSpacing: "0.08em",
                    }}
                  >
                    {c.attck}
                  </span>
                )}
              </div>
              <div className="name">{c.name}</div>
              <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 4 }}>{c.flavor}</div>
            </div>
          ))}
        </div>

        <h3>ATT&amp;CK coverage by team</h3>
        <div style={{ overflowX: "auto", marginBottom: 18 }}>
          <table className="attck-coverage">
            <thead>
              <tr>
                <th></th>
                {aar.attackChain.map((c) => (
                  <th key={c.id}>
                    <div className="t-id">{c.attck ?? "—"}</div>
                    <div className="t-stage">{c.stage}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {aar.perTeam.map((t) => (
                <tr key={t.teamId}>
                  <th scope="row">{t.teamName}</th>
                  {aar.attackChain.map((c) => {
                    const found = t.found.find((f) => f.cardId === c.id);
                    return (
                      <td key={c.id} className={found ? "hit" : "miss"}>
                        {found ? `✓ t${found.turn}` : "✗"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3>Per-team performance</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 14,
          }}
        >
          {aar.perTeam.map((t) => {
            const teamWon = t.teamId === aar.winningTeamId;
            const teamClosed = t.found.length >= 4;
            const teamMine = t.teamId === myTeam?.id;
            const tone = teamWon ? "won" : teamClosed ? "closed" : "failed";
            return (
              <div
                key={t.teamId}
                className="panel"
                style={{
                  background: "var(--color-bg-surface)",
                  borderColor:
                    tone === "won" ? "var(--color-accent)" : tone === "failed" ? "var(--color-bad)" : "var(--color-border)",
                }}
              >
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 14, marginBottom: 4 }}>
                  {t.teamName}{" "}
                  {teamMine && <span style={{ color: "var(--color-accent)", fontSize: 11, marginLeft: 4 }}>★ you</span>}
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: tone === "won" ? "var(--color-accent)" : tone === "failed" ? "var(--color-bad)" : "var(--color-text-muted)",
                    marginBottom: 8,
                  }}
                >
                  {tone === "won" ? "Contained — first" : tone === "closed" ? "Contained" : "Not contained"}
                </div>
                <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
                  {t.found.length}/4 stages · {t.turnsTaken} turns · pressure {t.pressure}
                </div>

                {t.lessons && t.lessons.length > 0 && (
                  <div className="lessons">
                    <div className="lessons-head">Coaching</div>
                    <ul>
                      {t.lessons.map((l, i) => (
                        <li key={i}>{l}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <details style={{ marginTop: 10 }}>
                  <summary
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 10,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      color: "var(--color-text-subtle)",
                      cursor: "pointer",
                    }}
                  >
                    Roll log ({t.proceduresPlayed.length})
                  </summary>
                  <div style={{ marginTop: 8, fontFamily: "var(--font-mono)", fontSize: 11 }}>
                    {t.proceduresPlayed.map((p, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, padding: "2px 0" }}>
                        <span style={{ width: 22, color: "var(--color-text-subtle)" }}>{p.turn}</span>
                        <span style={{ flex: 1 }}>{p.cardId.split(".").pop()}</span>
                        <span style={{ color: p.success ? "var(--color-good)" : "var(--color-bad)" }}>
                          {p.rolled}
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            );
          })}
        </div>

        <div
          style={{
            marginTop: 14,
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--color-text-subtle)",
          }}
        >
          {/* A debrief states the rules it was played under: runs on
              different rulesets are not comparable. */}
          ruleset {aar.rulesetVersion ?? "pre-1.0 (unversioned)"}
          {aar.replaySeed && (
            <>
              {" "}· replay seed: <code>{aar.replaySeed}</code>
            </>
          )}
        </div>
      </section>
    </>
  );
}
